import apiClient from './apiClient'
import type {
  Recognition,
  RecognitionFilters,
  PaginatedResponse,
  CreateRecognitionPayload,
  AddReactionPayload,
  AddCommentPayload,
  LeaderboardEntry,
  LeaderboardFilters,
  BadgeItem,
  Reward,
  RedeemRewardPayload,
  RecognitionAnalytics,
  DashboardData,
  Employee,
  Comment,
  RecognitionType,
} from '../types/recognition'

interface ApiResponse<T> {
  success: boolean
  message: string
  data: T
}

// Raw response shape from GET /recognition/recognitions
// IDs are strings to prevent JavaScript number precision loss for Snowflake IDs
interface RawRecognition {
  recognition_id: string | number
  account_id: string | number
  sender_id: string | number
  recognition_type: string
  message: string | { type: string; content: unknown[] }
  gif_url?: string | null
  points_awarded?: number | null
  status: string
  created_at: string
  sender_name?: string | null
  sender_email?: string | null
  // Full recipients array (returned by new backend)
  recipients?: Array<{ id: string; name: string }> | null
  // Legacy single-recipient fields (fallback)
  recipient_id?: string | number | null
  recipient_name?: string | null
  recipient_count?: number
  value?: string | null
  badge?: string | null
  comment_count?: number
  reaction_count?: number
  reactions?: { clap: number; heart: number; celebrate: number; fire: number }
  // Allow extra fields from backend
  [key: string]: unknown
}

interface RawRecognitionsResponse {
  data: RawRecognition[]
  success: boolean
  total: number
}

/** Extract plain text from a TipTap/ProseMirror JSON document */
function extractTextFromTipTap(doc: unknown): string {
  if (typeof doc === 'string') return doc
  if (!doc || typeof doc !== 'object') return ''
  const node = doc as { type?: string; text?: string; content?: unknown[] }
  if (node.text) return node.text
  if (Array.isArray(node.content)) {
    return node.content.map(extractTextFromTipTap).join('')
  }
  return ''
}

/** Normalize name to Title Case: "john doe" → "John Doe" */
function capitalizeName(name: string): string {
  if (!name) return name
  return name
    .split(' ')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(' ')
}

/** Build initials from a name string */
function makeInitials(name: string): string {
  return name
    .split(' ')
    .map((w) => w[0])
    .join('')
    .toUpperCase()
    .slice(0, 2) || '?'
}

/** Map frontend RecognitionType to DB-accepted uppercase values */
const TYPE_TO_DB: Record<string, string> = {
  kudos: 'KUDOS',
  spot_award: 'SPOT_AWARD',
  shoutout: 'SHOUT_OUT',
  thankyou: 'THANK_YOU',
  milestone: 'MILESTONE',
}
function toDbType(type: string): string {
  return TYPE_TO_DB[type] ?? type.toUpperCase()
}

/** Map DB enum values back to frontend RecognitionType */
const DB_TO_FRONTEND_TYPE: Record<string, string> = {
  kudos: 'kudos',
  spot_award: 'spot_award',
  shout_out: 'shoutout',
  thank_you: 'thankyou',
  milestone: 'milestone',
}
function fromDbType(type: string): string {
  return DB_TO_FRONTEND_TYPE[type.toLowerCase()] ?? type.toLowerCase()
}

/** Format ISO date to relative time string */
function formatRelativeTime(iso: string): string {
  const date = new Date(iso)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffMins = Math.floor(diffMs / 60000)
  if (diffMins < 1) return 'Just now'
  if (diffMins < 60) return `${diffMins} min ago`
  const diffHours = Math.floor(diffMins / 60)
  if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`
  const diffDays = Math.floor(diffHours / 24)
  if (diffDays === 1) return 'Yesterday'
  if (diffDays < 7) return `${diffDays} days ago`
  return date.toLocaleDateString()
}

/** Map a raw backend recognition to the frontend Recognition type */
function mapRawRecognition(raw: RawRecognition): Recognition {
  const senderName = capitalizeName(raw.sender_name || 'Unknown')
  const messageText = extractTextFromTipTap(raw.message)

  // Build recipients array from new backend field, falling back to legacy single-recipient
  const recipients: Recognition['recipients'] =
    raw.recipients && raw.recipients.length > 0
      ? raw.recipients.map((r) => {
        const name = capitalizeName(r.name || 'Unknown')
        return { id: String(r.id), name, dept: '', team: '', title: '', initials: makeInitials(name) }
      })
      : raw.recipient_id
        ? (() => {
          const name = capitalizeName(raw.recipient_name || 'Unknown')
          return [{ id: String(raw.recipient_id), name, dept: '', team: '', title: '', initials: makeInitials(name) }]
        })()
        : []

  return {
    id: String(raw.recognition_id),
    sender: {
      id: String(raw.sender_id ?? ''),
      name: senderName,
      dept: '',
      team: '',
      title: '',
      initials: makeInitials(senderName),
    },
    recipients,
    type: fromDbType(raw.recognition_type || 'kudos') as Recognition['type'],
    message: messageText,
    value: raw.value || null,
    badge: raw.badge || null,
    reactions: raw.reactions ?? { clap: 0, heart: 0, celebrate: 0, fire: 0 },
    comments: [],
    time: raw.created_at ? formatRelativeTime(raw.created_at) : '',
    points: raw.points_awarded ?? undefined,
  }
}

// Response shape from Core-Backend POST /recognition/employees
// Mirrors dto.UserResponse — same fields as /admin/users for consistent filtering.
interface RecognitionEmployeesResponse {
  success: boolean
  data: {
    payload: {
      employee_id: string
      fname: string
      lname?: string | null
      employee_name?: string | null
      email?: string | null
      profile_pic_url?: string | null
      job_title?: string | null
      job_grade?: string | null
      employment_status?: string | null
      employment_type?: string | null
      employee_code?: string | null
      phone?: string | null
      manager_id?: string | null
      primary_org_unit_id?: string | null
      primary_org_unit_name?: string | null
      primary_org_unit_level?: string | null
      primary_org_unit_position?: number | null
      org_path?: Array<{ id: string; name: string; level: string }> | null
      unit_name?: string | null  // legacy alias = primary_org_unit_name
    }[]
    totalCount: number
  }
}

const BASE = '/recognition'

export async function getRecognitions(
  filters?: RecognitionFilters,
): Promise<PaginatedResponse<Recognition>> {
  const { view, unit_id, ...restFilters } = filters || {}
  const { data } = await apiClient.get<RawRecognitionsResponse>(
    `${BASE}/recognitions`,
    { params: { ...restFilters, ...(view ? { view } : {}), ...(unit_id ? { unit_id } : {}) } },
  )
  const mapped = (data.data ?? []).map(mapRawRecognition)
  return {
    data: mapped,
    meta: {
      total: data.total ?? mapped.length,
      page: filters?.page ?? 1,
      per_page: filters?.perPage ?? 20,
      total_pages: Math.ceil((data.total ?? mapped.length) / (filters?.perPage ?? 20)),
    },
  }
}

export async function createRecognition(
  payload: CreateRecognitionPayload,
): Promise<Recognition[]> {
  // Transform frontend payload to backend format
  // IDs are sent as strings to prevent JavaScript number precision loss for Snowflake IDs
  const backendPayload = {
    recipient_ids: payload.recipient_ids,
    recognition_type: toDbType(payload.type),
    message: {
      type: 'doc',
      content: [{ type: 'paragraph', content: [{ type: 'text', text: payload.message }] }],
    },
    value_ids: payload.value_ids ?? [],
    points_awarded: payload.points ?? 0,
  }
  const { data } = await apiClient.post<{ success: boolean; data: RawRecognition | RawRecognition[] }>(`${BASE}/recognitions`, backendPayload)
  // Backend may return a single object or an array
  const rawItems = Array.isArray(data.data) ? data.data : data.data ? [data.data] : []
  return rawItems.map(mapRawRecognition)
}

export async function addReaction(payload: AddReactionPayload): Promise<{ added: boolean }> {
  const { data } = await apiClient.post<{ data: { added: boolean }; success: boolean }>(
    `${BASE}/recognitions/${payload.recognition_id}/reactions`,
    { emoji: payload.reaction_type },
  )
  return { added: data.data.added }
}

export async function addComment(payload: AddCommentPayload): Promise<Comment> {
  const { data } = await apiClient.post<ApiResponse<Comment>>(
    `${BASE}/recognitions/${payload.recognition_id}/comments`,
    { content: payload.text },
  )
  return data.data
}

// Raw shape returned by the backend GET /recognition/leaderboard (V2)
interface RawLeaderboardItem {
  rank: number
  employee_id: number | string
  employee_name: string
  employee_email?: string | null
  department_name?: string | null
  recognition_count: number
  points: number
  top_value_name?: string | null
  total_xp: number
}

export async function getLeaderboard(
  filters?: LeaderboardFilters,
): Promise<{ data: LeaderboardEntry[]; total: number }> {
  const response = await apiClient.get<{ success: boolean; data: RawLeaderboardItem[]; total: number }>(
    `${BASE}/leaderboard`,
    {
      params: { period: filters?.period, page_size: filters?.page_size, page_offset: filters?.page_offset },
      // Parse employee_id as string to avoid JS number precision loss on snowflake IDs
      transformResponse: [(data: string) => {
        try {
          // Replace employee_id numeric values with strings before JSON.parse
          const fixed = data.replace(/"employee_id"\s*:\s*(\d+)/g, '"employee_id":"$1"')
          return JSON.parse(fixed)
        } catch {
          return JSON.parse(data)
        }
      }],
    },
  )
  const raw = response?.data
  if (!raw || typeof raw !== 'object' || !Array.isArray(raw.data)) {
    console.warn('⚠️ Leaderboard API returned unexpected response:', raw)
    return { data: [], total: 0 }
  }
  const items = raw.data.map((item): LeaderboardEntry => {
    const empName = typeof item.employee_name === 'string' ? item.employee_name : String(item.employee_name ?? 'Unknown')
    const deptName = typeof item.department_name === 'string' ? item.department_name : String(item.department_name ?? '')
    const topVal = typeof item.top_value_name === 'string' ? item.top_value_name : item.top_value_name != null ? String(item.top_value_name) : null
    return {
      rank: item.rank,
      employee: {
        id: String(item.employee_id),
        name: capitalizeName(empName || 'Unknown'),
        dept: deptName || '',
        team: '',
        title: '',
        initials: makeInitials(empName || 'Unknown'),
      },
      count: item.recognition_count ?? 0,
      points: item.points ?? 0,
      xp: item.total_xp ?? 0,
      topValue: topVal || null,
      trend: 'same' as LeaderboardEntry['trend'],
    }
  })

  return { data: items, total: raw.total ?? items.length }
}

export async function getBadges(category?: string): Promise<BadgeItem[]> {
  const { data } = await apiClient.get<ApiResponse<BadgeItem[]>>(
    `${BASE}/badges`,
    { params: category ? { category } : undefined },
  )
  return data.data
}

export async function getRewards(category?: string): Promise<Reward[]> {
  const { data } = await apiClient.get<ApiResponse<Reward[]>>(
    `${BASE}/rewards`,
    { params: category ? { category } : undefined },
  )
  return data.data
}

export async function redeemReward(payload: RedeemRewardPayload): Promise<void> {
  await apiClient.post(`${BASE}/rewards/${payload.reward_id}/redeem`, payload)
}

export async function getAnalytics(): Promise<RecognitionAnalytics> {
  const { data } = await apiClient.get<ApiResponse<RecognitionAnalytics>>(
    `${BASE}/analytics/overview`,
  )
  return data.data
}

/** Fetch the unified dashboard sidebar data: My Stats + Trending Values + Top Recognized */
export async function getDashboard(): Promise<DashboardData> {
  // Raw backend response has different field names for my_stats
  interface RawDashboard {
    my_stats: {
      received_count: number
      given_count: number
      points_balance: number
      total_xp: number
    }
    trending_values: DashboardData['trending_values']
    top_recognized: DashboardData['top_recognized']
  }

  const { data } = await apiClient.get<ApiResponse<RawDashboard>>(
    `${BASE}/dashboard`,
  )
  const raw = data?.data
  // Guard against missing or malformed dashboard response
  if (!raw || typeof raw !== 'object') {
    console.warn('⚠️ Dashboard API returned unexpected response:', typeof raw, raw)
    return { my_stats: { received: 0, given: 0, points: 0, xp: 0 }, trending_values: [], top_recognized: [] }
  }
  const stats = raw.my_stats ?? { received_count: 0, given_count: 0, points_balance: 0, total_xp: 0 }
  return {
    my_stats: {
      received: stats.received_count ?? 0,
      given: stats.given_count ?? 0,
      points: stats.points_balance ?? 0,
      xp: stats.total_xp ?? 0,
    },
    trending_values: raw.trending_values ?? [],
    top_recognized: (raw.top_recognized ?? [])
      .filter((e, idx, arr) => arr.findIndex((x) => x.employee_id === e.employee_id) === idx)
      .map((e) => ({
        ...e,
        employee_name: capitalizeName(e.employee_name ?? ''),
      })),
  }
}

/**
 * Fetch employees based on recognition type with RBAC via Core-Backend.
 * The backend applies RBAC scope filtering (manager reportees, HR scope, etc.)
 */
export async function fetchEmployeesForRecognition(
  _recognitionType: RecognitionType,
): Promise<Employee[]> {

  // Call the Core-Backend RBAC-aware employee endpoint
  const { data } = await apiClient.post<RecognitionEmployeesResponse>(
    `${BASE}/employees`,
    { recognition_type: _recognitionType, page_num: 1, page_count: 100 },
  )
  // Map the response to Employee type, filtering out duplicates
  const seen = new Set<string>()
  return (data.data?.payload ?? [])
    .filter((emp) => emp.employee_id != null)
    .map((emp) => {
      const name = capitalizeName(
        emp.employee_name ||
        [emp.fname, emp.lname].filter(Boolean).join(' ')
      )
      // Use primary_org_unit_name as team (same as /admin/users primary unit)
      const orgUnitName = emp.primary_org_unit_name || emp.unit_name || ''
      return {
        id: String(emp.employee_id),
        name,
        dept: '',      // department not returned by UserService (use org_path for hierarchy)
        division: emp.primary_org_unit_level === 'Division' ? orgUnitName : undefined,
        title: emp.job_title || '',
        team: orgUnitName,
        manager: emp.manager_id || undefined,
        employee_code: emp.employee_code || undefined,
        employment_type: emp.employment_type || undefined,
        avatar: emp.profile_pic_url || null,
        initials: name
          .split(' ')
          .map((w: string) => w[0])
          .join('')
          .toUpperCase()
          .slice(0, 2),
        org_assignments: Array.isArray(emp.org_path)
          ? emp.org_path.map((p) => ({
            scope_instance_id: p.id,
            scope_name: p.name,
            level_name: p.level,
            level_position: 0,
            assignment_type: 'PRIMARY',
            effective_from: '',
            effective_to: null,
          }))
          : [],
      }
    })
    .filter((emp) => {
      if (seen.has(emp.id)) return false
      seen.add(emp.id)
      return true
    })
}


interface CompanyValueResponse {
  value_id: number | string
  name: string
  description?: string | null
  icon?: string | null
  color?: string | null
  sort_order: number
  is_active: boolean
}

export interface CompanyValueOption {
  id: string
  name: string
  color?: string | null
}

export async function getCompanyValues(): Promise<CompanyValueOption[]> {
  const { data } = await apiClient.get<ApiResponse<CompanyValueResponse[]>>(
    `${BASE}/admin/values`,
  )
  return (data.data ?? []).filter((v) => v.is_active).map((v) => ({
    id: String(v.value_id),
    name: v.name,
    color: v.color ?? null,
  }))
}

export interface CreateCompanyValuePayload {
  name: string
  icon?: string
  color?: string
  sort_order?: number
}

export async function deleteCompanyValue(valueId: string): Promise<void> {
  await apiClient.delete(`${BASE}/admin/values/${valueId}`)
}

export async function createCompanyValue(payload: CreateCompanyValuePayload): Promise<CompanyValueOption> {
  const { data } = await apiClient.post<ApiResponse<CompanyValueResponse>>(
    `${BASE}/admin/values`,
    payload,
  )
  const v = data.data
  return {
    id: String(v.value_id),
    name: v.name,
    color: v.color ?? null,
  }
}

export const recognitionApi = {
  getRecognitions,
  createRecognition,
  addReaction,
  addComment,
  getLeaderboard,
  getBadges,
  getRewards,
  redeemReward,
  getAnalytics,
  getDashboard,
  fetchEmployeesForRecognition,
  getCompanyValues,
  createCompanyValue,
  deleteCompanyValue,
}

export default recognitionApi

